import { z } from 'zod';

export const LOCALES = ['en', 'uk', 'ru'] as const;
export const DEFAULT_LOCALE = 'en' satisfies Locale;
export const Locale = z.enum(LOCALES);
export type Locale = z.infer<typeof Locale>;

/**
 * Built-in avatars: pennants in the two fleet colours plus a few neutral ones,
 * and flags of the International Code of Signals. No national symbols.
 * The web client maps each id to an SVG; ids are stored in `users.avatar`.
 */
export const AVATARS = [
  'pennant-yellow',
  'pennant-green',
  'pennant-crimson',
  'pennant-slate',
  'burgee-gold',
  'burgee-blue',
  'signal-alpha',
  'signal-bravo',
  'signal-delta',
  'signal-kilo',
  'signal-november',
  'signal-papa',
  'signal-uniform',
  'signal-zulu',
] as const;
export const DEFAULT_AVATAR = 'pennant-yellow' satisfies Avatar;
export const Avatar = z.enum(AVATARS);
export type Avatar = z.infer<typeof Avatar>;

/**
 * First-time hints of the game screen (docs/ux.md U-LEARN-06): each one is shown to a captain
 * once, the first time its mechanic comes up in their own game.
 */
export const HINT_KEYS = [
  'muster',
  'wind',
  'becalmed',
  'storm',
  'fire',
  'volley',
  'damaged',
  'dock',
  'fort',
  'royalPort',
] as const;
export const HintKey = z.enum(HINT_KEYS);
export type HintKey = z.infer<typeof HintKey>;

/** The two sides of a game, as the rules engine names them; each player picks a colour apart. */
export const FLEET_SIDES = ['red', 'blue'] as const;
export const FleetSide = z.enum(FLEET_SIDES);
export type FleetSide = z.infer<typeof FleetSide>;

export const IsoDateTime = z.iso.datetime({ offset: true });

export const ERROR_CODES = [
  'unauthorized',
  'forbidden',
  'not_found',
  'validation_failed',
  'nickname_taken',
  'rule_violation',
  'version_conflict',
  'invite_taken',
  'invite_expired',
  'internal',
] as const;
export const ErrorCode = z.enum(ERROR_CODES);
export type ErrorCode = z.infer<typeof ErrorCode>;

export const ApiError = z.object({
  error: z.object({
    code: ErrorCode,
    message: z.string(),
    details: z.unknown().optional(),
  }),
});
export type ApiError = z.infer<typeof ApiError>;
